const HEIGHT_PROPERTY = '--announcement-bar-height';
const HEIGHT_OWNER = Symbol.for('sigma.announcementHeightOwner');
const MIN_INTERVAL_SECONDS = 3;
const MAX_INTERVAL_SECONDS = 15;
const DEFAULT_INTERVAL_SECONDS = 6;

class SigmaAnnouncement extends HTMLElement {
  connectedCallback() {
    if (this.abortController) return;

    this.abortController = new AbortController();
    this.slides = Array.from(this.querySelectorAll('[data-announcement-slide]'));
    this.controls = this.querySelector('[data-announcement-controls]');
    this.previousButton = this.querySelector('[data-announcement-previous]');
    this.nextButton = this.querySelector('[data-announcement-next]');
    this.toggleButton = this.querySelector('[data-announcement-toggle]');
    this.pauseIcon = this.querySelector('[data-announcement-pause-icon]');
    this.playIcon = this.querySelector('[data-announcement-play-icon]');
    this.status = this.querySelector('[data-announcement-status]');
    this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.mode = this.dataset.mode === 'rotate' ? 'rotate' : 'static';
    this.rotationEnabled = this.mode === 'rotate' && this.slides.length >= 2;
    this.interval = this.readInterval();
    this.currentIndex = 0;
    this.userPaused = false;
    this.reducedMotionOverride = false;
    this.pointerPaused = false;
    this.focusPaused = this.matches(':focus-within');
    this.editorSelection = null;
    this.timer = 0;
    this.heightValue = '';
    this.heightOwner = {};
    this.resizeObserver = null;

    this.controls?.toggleAttribute('hidden', !this.rotationEnabled);
    this.showSlide(0);
    this.toggleAttribute('data-announcement-motion', !window.Shopify?.designMode);
    this.updateToggle();
    this.bindEvents();
    this.observeHeight();
    this.refreshTimer();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;
    this.clearTimer();
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.releaseHeightOwnership();
  }

  readInterval() {
    const seconds = Number.parseFloat(this.dataset.interval);
    if (!Number.isFinite(seconds)) return DEFAULT_INTERVAL_SECONDS * 1000;

    return Math.min(Math.max(seconds, MIN_INTERVAL_SECONDS), MAX_INTERVAL_SECONDS) * 1000;
  }

  bindEvents() {
    const { signal } = this.abortController;

    this.previousButton?.addEventListener('click', () => this.browse(-1), { signal });
    this.nextButton?.addEventListener('click', () => this.browse(1), { signal });
    this.toggleButton?.addEventListener('click', () => this.toggleAutoplay(), { signal });

    this.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'touch') return;
      this.pointerPaused = true;
      this.refreshTimer();
    }, { signal });

    this.addEventListener('pointerleave', (event) => {
      if (event.pointerType === 'touch') return;
      this.pointerPaused = false;
      this.refreshTimer();
    }, { signal });

    this.addEventListener('focusin', () => {
      this.focusPaused = true;
      this.refreshTimer();
    }, { signal });

    this.addEventListener('focusout', (event) => {
      if (this.contains(event.relatedTarget)) return;
      this.focusPaused = false;
      this.refreshTimer();
    }, { signal });

    document.addEventListener('visibilitychange', () => this.refreshTimer(), { signal });
    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', (event) => this.selectEditorBlock(event), { signal });
      document.addEventListener('shopify:block:deselect', (event) => this.deselectEditorBlock(event), { signal });
    }

    const handleMotionChange = () => {
      if (this.motionPreference.matches) this.reducedMotionOverride = false;
      this.updateToggle();
      this.refreshTimer();
    };

    this.motionPreference.addEventListener('change', handleMotionChange, { signal });
  }

  browse(offset) {
    if (!this.rotationEnabled) return;

    this.userPaused = true;
    this.reducedMotionOverride = false;
    this.rememberEditorPreference();
    this.showSlide(this.currentIndex + offset, { announce: true });
    this.updateToggle();
    this.refreshTimer();
  }

  toggleAutoplay() {
    if (!this.rotationEnabled) return;

    if (this.autoplayRequested()) {
      this.userPaused = true;
      this.reducedMotionOverride = false;
    } else {
      this.userPaused = false;
      this.reducedMotionOverride = this.motionPreference.matches;
    }

    this.rememberEditorPreference();
    this.updateToggle();
    this.refreshTimer();
  }

  rememberEditorPreference() {
    if (!this.editorSelection) return;

    this.editorSelection.userPaused = this.userPaused;
    this.editorSelection.reducedMotionOverride = this.reducedMotionOverride;
  }

  autoplayRequested() {
    return this.rotationEnabled
      && !this.userPaused
      && (!this.motionPreference.matches || this.reducedMotionOverride);
  }

  canAutoplay() {
    return this.autoplayRequested()
      && !this.pointerPaused
      && !this.focusPaused
      && !document.hidden
      && !window.Shopify?.designMode
      && !this.editorSelection;
  }

  updateToggle() {
    const playing = this.autoplayRequested();

    if (this.toggleButton) {
      this.toggleButton.setAttribute(
        'aria-label',
        playing ? 'Pause announcements' : 'Play announcements',
      );
    }

    this.pauseIcon?.toggleAttribute('hidden', !playing);
    this.playIcon?.toggleAttribute('hidden', playing);
  }

  refreshTimer() {
    this.clearTimer();
    if (!this.canAutoplay()) return;

    this.timer = window.setTimeout(() => {
      this.timer = 0;
      this.showSlide(this.currentIndex + 1);
      this.refreshTimer();
    }, this.interval);
  }

  clearTimer() {
    window.clearTimeout(this.timer);
    this.timer = 0;
  }

  showSlide(index, { announce = false } = {}) {
    if (this.slides.length === 0) return;

    const slideCount = this.slides.length;
    const nextIndex = ((index % slideCount) + slideCount) % slideCount;
    this.currentIndex = nextIndex;

    this.slides.forEach((slide, slideIndex) => {
      const visible = slideIndex === nextIndex;
      slide.toggleAttribute('hidden', !visible);
      slide.inert = !visible;
      slide.setAttribute('aria-hidden', String(!visible));
    });

    if (announce && this.status) {
      const message = this.slides[nextIndex].textContent.trim();
      this.status.textContent = `Message ${nextIndex + 1} of ${slideCount}: ${message}`;
    }
  }

  selectEditorBlock(event) {
    const sectionId = String(event.detail?.sectionId ?? '');
    const blockId = String(event.detail?.blockId ?? '');
    if (!sectionId || sectionId !== this.dataset.sectionId || !blockId) return;

    const slideIndex = this.slides.findIndex((slide) => slide.dataset.blockId === blockId);
    if (slideIndex < 0) return;

    if (!this.editorSelection) {
      this.editorSelection = {
        blockId,
        index: this.currentIndex,
        reducedMotionOverride: this.reducedMotionOverride,
        userPaused: this.userPaused,
      };
    } else {
      this.editorSelection.blockId = blockId;
    }

    this.showSlide(slideIndex);
    this.updateToggle();
    this.refreshTimer();
  }

  deselectEditorBlock(event) {
    if (!this.editorSelection) return;

    const sectionId = String(event.detail?.sectionId ?? '');
    const blockId = String(event.detail?.blockId ?? '');
    if (sectionId !== this.dataset.sectionId || blockId !== this.editorSelection.blockId) return;

    const previous = this.editorSelection;
    this.editorSelection = null;
    this.userPaused = previous.userPaused;
    this.reducedMotionOverride = previous.reducedMotionOverride;
    this.showSlide(this.mode === 'static' ? 0 : previous.index);
    this.updateToggle();
    this.refreshTimer();
  }

  observeHeight() {
    window[HEIGHT_OWNER] = this.heightOwner;
    this.updateHeight();

    if (typeof ResizeObserver === 'function') {
      this.resizeObserver = new ResizeObserver(() => this.updateHeight());
      this.resizeObserver.observe(this);
      return;
    }

    window.addEventListener('resize', () => this.updateHeight(), {
      passive: true,
      signal: this.abortController.signal,
    });
  }

  updateHeight() {
    if (window[HEIGHT_OWNER] !== this.heightOwner || !document.body) return;

    const value = `${Math.ceil(this.getBoundingClientRect().height)}px`;
    if (value === this.heightValue) return;

    this.heightValue = value;
    document.body.style.setProperty(HEIGHT_PROPERTY, value);
  }

  releaseHeightOwnership() {
    if (window[HEIGHT_OWNER] !== this.heightOwner) return;

    if (document.body?.style.getPropertyValue(HEIGHT_PROPERTY) === this.heightValue) {
      document.body.style.removeProperty(HEIGHT_PROPERTY);
    }

    delete window[HEIGHT_OWNER];
  }
}

if (!customElements.get('sigma-announcement')) {
  customElements.define('sigma-announcement', SigmaAnnouncement);
}
